'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  caseType: string;
}

const testimonials: Testimonial[] = [
{
  id: 1,
  name: 'Michael Thompson',
  role: 'Estate Litigation Client',
  image: "/assets/images/testimonial_michael_thompson_v2.png",
  alt: 'Professional headshot of middle-aged Caucasian man with gray hair in dark suit smiling confidently',
  rating: 5,
  text: 'Zandoria and her team handled my complex estate dispute with exceptional professionalism and expertise. They fought tirelessly to protect my inheritance rights and achieved an outcome that exceeded my expectations. I cannot recommend Lubbert Associates highly enough.',
  caseType: 'Probate & Estate Litigation'
},
{
  id: 2,
  name: 'Sarah Martinez',
  role: 'Personal Injury Client',
  image: "/assets/images/testimonial_sarah_martinez_v2.png",
  alt: 'Professional portrait of Hispanic woman with long dark hair in white blouse smiling warmly',
  rating: 5,
  text: 'After my car accident, I felt overwhelmed and unsure where to turn. Lubbert Associates provided compassionate guidance throughout the entire process and secured a settlement that covered all my medical expenses and lost wages. They truly care about their clients.',
  caseType: 'Personal Injury Law'
},
{
  id: 3,
  name: 'David Chen',
  role: 'Business Litigation Client',
  image: "/assets/images/testimonial_david_chen_v2.png",
  alt: 'Professional headshot of Asian businessman in navy suit with glasses smiling confidently in modern office',
  rating: 5,
  text: 'When my business partner breached our agreement, Lubbert Associates stepped in with strategic legal counsel that protected my company\'s interests. Their expertise in contract law and business litigation was invaluable. Highly professional and results-driven.',
  caseType: 'Contracts & Business Agreements'
},
{
  id: 4,
  name: 'Jennifer Williams',
  role: 'Insurance Dispute Client',
  image: "/assets/images/testimonial_jennifer_williams_v2.png",
  alt: 'Professional portrait of African American woman with short curly hair in burgundy blazer smiling warmly',
  rating: 5,
  text: 'My insurance company denied my legitimate claim, leaving me frustrated and financially strained. Lubbert Associates fought the bad faith denial and secured the full coverage I was entitled to. Their knowledge of insurance law is outstanding.',
  caseType: 'Insurance Law'
},
{
  id: 5,
  name: 'Robert Anderson',
  role: 'Estate Planning Client',
  image: "/assets/images/testimonial_robert_anderson_v2.png",
  alt: 'Professional headshot of senior Caucasian man with white hair in charcoal suit smiling kindly',
  rating: 5,
  text: 'The team at Lubbert Associates helped me navigate a complicated will contest with patience and expertise. They kept me informed every step of the way and achieved a fair resolution that honored my father\'s true wishes. Exceptional legal representation.',
  caseType: 'Probate & Estate Litigation'
},
{
  id: 6,
  name: 'Lisa Rodriguez',
  role: 'Personal Injury Client',
  image: "/assets/images/testimonial_lisa_rodriguez_v2.png",
  alt: 'Professional portrait of Hispanic woman with long brown hair in teal blouse smiling confidently',
  rating: 5,
  text: 'After suffering a serious slip and fall injury, I was worried about mounting medical bills. Lubbert Associates took on my case with no upfront fees and secured compensation that covered everything. They are true advocates for injury victims.',
  caseType: 'Personal Injury Law'
}];


export default function ClientTestimonials() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  if (!isHydrated) {
    return (
      <section className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="h-8 w-64 bg-accent/10 rounded mx-auto mb-4 animate-pulse" />
            <div className="h-12 w-96 bg-primary/10 rounded mx-auto mb-6 animate-pulse" />
            <div className="h-6 w-3/4 bg-text-secondary/10 rounded mx-auto animate-pulse" />
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-12 shadow-elevation-md">
              <div className="flex items-center gap-6 mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full animate-pulse" />
                <div className="flex-1 space-y-3">
                  <div className="h-6 w-48 bg-primary/10 rounded animate-pulse" />
                  <div className="h-4 w-32 bg-text-secondary/10 rounded animate-pulse" />
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-4 w-full bg-text-secondary/10 rounded animate-pulse" />
                <div className="h-4 w-full bg-text-secondary/10 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-text-secondary/10 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-accent/10 border border-accent/30 rounded-full text-accent text-sm font-semibold tracking-wider uppercase mb-4">
            Client Success Stories
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary leading-tight mb-6">
            Trusted by Clients,
            <span className="text-accent"> Proven by Results</span>
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed">
            Real stories from real clients who experienced the Lubbert Associates difference.
            Our commitment to excellence is reflected in every case we handle.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-elevation-md border border-border">
              <div className="flex flex-col md:flex-row items-start gap-8 mb-8">
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-accent/20">
                    <AppImage
                      src={currentTestimonial.image}
                      alt={currentTestimonial.alt}
                      className="w-full h-full object-cover" />

                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-accent rounded-full flex items-center justify-center shadow-subtle">
                    <Icon name="CheckBadgeIcon" size={24} className="text-white" variant="solid" />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(currentTestimonial.rating)].map((_, i) =>
                    <Icon
                      key={i}
                      name="StarIcon"
                      size={20}
                      className="text-accent"
                      variant="solid" />

                    )}
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-primary mb-1">
                    {currentTestimonial.name}
                  </h3>
                  <p className="text-text-secondary font-medium mb-2">
                    {currentTestimonial.role}
                  </p>
                  <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full border border-accent/20">
                    {currentTestimonial.caseType}
                  </span>
                </div>
              </div>

              <div className="relative">
                <Icon
                  name="ChatBubbleLeftIcon"
                  size={48}
                  className="text-accent/10 absolute -top-4 -left-2"
                  variant="solid" />

                <p className="text-lg text-text-primary leading-relaxed relative z-10 pl-8">
                  "{currentTestimonial.text}"
                </p>
              </div>
            </div>

            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 w-12 h-12 bg-white rounded-full shadow-elevation-sm hover:shadow-elevation-md flex items-center justify-center text-primary hover:text-accent transition-all duration-300 border border-border hover:border-accent/30"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeftIcon" size={24} variant="outline" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 w-12 h-12 bg-white rounded-full shadow-elevation-sm hover:shadow-elevation-md flex items-center justify-center text-primary hover:text-accent transition-all duration-300 border border-border hover:border-accent/30"
              aria-label="Next testimonial">

              <Icon name="ChevronRightIcon" size={24} variant="outline" />
            </button>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex ?
              'bg-accent w-8' : 'bg-border hover:bg-accent/50'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 max-w-5xl mx-auto">
          {[
          {
            icon: 'UserGroupIcon',
            stat: '500+',
            label: 'Satisfied Clients'
          },
          {
            icon: 'TrophyIcon',
            stat: '98%',
            label: 'Success Rate'
          },
          {
            icon: 'StarIcon',
            stat: '4.9/5',
            label: 'Average Rating'
          }].
          map((item, index) =>
          <div
            key={index}
            className="text-center p-6 bg-white rounded-xl shadow-subtle border border-border">

              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                name={item.icon as any}
                size={28}
                className="text-accent"
                variant="solid" />

              </div>
              <div className="text-4xl font-bold text-primary font-serif mb-2">{item.stat}</div>
              <div className="text-text-secondary font-medium">{item.label}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

}